import { z } from "zod";
import { HHMM_PATTERN } from "@carry/shared";

// 21 文字 nanoid (プレフィックスなし、URL-safe)
export const PublicIdSchema = z
  .string()
  .regex(/^[A-Za-z0-9_-]{21}$/, { message: "public_id は 21 文字の nanoid 形式であること" });

/**
 * 時刻 ("HH:MM"、24 時間表記・ゼロ埋め) を受ける入力フィールド (#751)。
 *
 * **形式の集合は `@carry/shared` の `HHMM_PATTERN` が単一源泉**。contract に自前の正規表現を
 * 置くと、通る値の集合が各所でずれる (#733 で区切り文字を 2 箇所に持って踏んだのと同じ)。
 *
 * ここを素の `z.string()` にしていると `9:20` / `12:75` / `25:00` が保存まで到達し、
 * `new Date("2026-05-01T9:20:00+09:00")` が Invalid Date → NaN になって libSQL が弾く。
 * 項目を名指しできる 400 になるべきところが、時刻に触れない 500 になる。
 *
 * **空文字は「未入力」として受理する。** null / undefined / "" のどれも DB では同じ null に
 * 落ちる (`timeStrToDate` が falsy を null にする) ため、区別しても意味が無く、空欄をそのまま
 * 送る呼び出しを 400 にすると既存画面が壊れる。null / undefined を許すかは各フィールドの
 * `.nullable()` / `.optional()` 側で決める。
 */
export const TimeOfDaySchema = z.string().refine((v) => v === "" || HHMM_PATTERN.test(v), {
  message: "時刻は HH:MM 形式 (00:00〜23:59) で入力してください",
});

/**
 * DB の Time 列で使う時刻入力。`HH:MM` に加え、既存の admin が送る `HH:MM:SS` を受ける (#805)。
 *
 * 時・分の検査は {@link TimeOfDaySchema} / `HHMM_PATTERN` に委譲し、秒だけを追加で確認する。
 * これにより時刻の範囲定義を重複させず、`25:00` / `12:75` が最適化時に黙って無効化されるのを防ぐ。
 */
export const TimeOfDayWithSecondsSchema = z.string().refine(
  (value) => {
    if (value === "" || HHMM_PATTERN.test(value)) return true;
    if (value.length !== 8 || value[5] !== ":") return false;

    const seconds = value.slice(6);
    return (
      HHMM_PATTERN.test(value.slice(0, 5)) &&
      seconds.length === 2 &&
      [...seconds].every((digit) => digit >= "0" && digit <= "9") &&
      Number(seconds) <= 59
    );
  },
  {
    message: "時刻は HH:MM または HH:MM:SS 形式 (00:00〜23:59) で入力してください",
  },
);

/**
 * 文字列比較で突き合わせられる "HH:MM" か (#751)。未入力・形式不正はどちらも false。
 *
 * 時刻どうしの前後関係 (集荷 < 配送、0 時またぎ) は**ゼロ埋めされた "HH:MM" 同士**でしか
 * 成り立たない。`"9:20" > "10:00"` になるため、形式不正な値をそのまま比較すると
 * 「0時をまたぐ時間帯」のような無関係なメッセージが形式エラーに重なって出て、
 * どの項目をどう直せばよいのかが読めなくなる。
 */
export const isTimeOfDay = (value: unknown): value is string =>
  typeof value === "string" && HHMM_PATTERN.test(value);

export const PaginationRequest = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(20),
  cursor: z.string().optional(),
  sort_by: z.string().default("created_at"),
  sort_order: z.enum(["asc", "desc"]).default("desc"),
});

export const PaginationResponse = z.object({
  total: z.number().int().min(0),
  limit: z.number().int().min(1),
  has_more: z.boolean(),
  next_cursor: z.string().nullable(),
});
